// Protects /account* routes and provides shared shell.
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LogOut, User, ClipboardList, Sparkles, Home } from "lucide-react";
import { useCustomerAuth } from "@/lib/customerAuth";
import { toast } from "sonner";

const NAV = [
    { to: "/account", label: "Overview", icon: Home, end: true, testid: "account-nav-overview" },
    { to: "/account/bookings", label: "My bookings", icon: ClipboardList, testid: "account-nav-bookings" },
    { to: "/account/loyalty", label: "Cove Rewards", icon: Sparkles, testid: "account-nav-loyalty" },
    { to: "/account/profile", label: "Profile", icon: User, testid: "account-nav-profile" },
];

export default function AccountLayout() {
    const { user, loading, logout } = useCustomerAuth();
    const location = useLocation();

    if (loading) return <div className="min-h-screen bg-ivory flex items-center justify-center text-navy/60 text-sm">Loading your account…</div>;
    if (!user) return <Navigate to="/signin" state={{ from: location }} replace />;

    const doLogout = async () => {
        await logout();
        toast.success("Signed out");
    };

    return (
        <div className="min-h-screen bg-ivory pt-20" data-testid="account-layout">
            <div className="max-w-6xl mx-auto px-6 py-10">
                <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.32em] text-gold">My Serenity Cove</div>
                        <h1 className="font-display text-4xl md:text-5xl text-navy mt-3">Welcome, {user.name?.split(" ")[0] || "Guest"}</h1>
                        <p className="text-navy/60 mt-2 text-sm max-w-lg">
                            {user.loyalty?.tier} · {user.loyalty?.available || 0} available points
                            {!user.email_verified && (
                                <span className="ml-3 inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-[0.14em]">
                                    Email unverified
                                </span>
                            )}
                        </p>
                    </div>
                    <button onClick={doLogout} data-testid="account-logout" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full border border-navy/20 text-navy hover:bg-navy hover:text-ivory transition-colors">
                        <LogOut size={13} /> Sign out
                    </button>
                </header>

                <div className="grid md:grid-cols-[240px_1fr] gap-8">
                    <aside>
                        <nav className="bg-white border border-navy/10 rounded-2xl p-2 sticky top-24">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.end}
                                    data-testid={n.testid}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${isActive ? "bg-navy text-ivory" : "text-navy/70 hover:bg-navy/5"}`
                                    }
                                >
                                    <n.icon size={14} /> {n.label}
                                </NavLink>
                            ))}
                        </nav>
                    </aside>

                    <main>
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    );
}
